import React, { useRef, useState, useEffect } from "react";
import {
  X,
  Plus,
  Minus,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  formatYMD,
  diffYMD,
  effectiveDurationYMD30360,
  monthsBetween30360,
} from "../shared";

/** YYYY-MM-DD or YYYY-MM → DD.MM.YYYY or MM.YYYY. */
function formatGermanDate(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const m2 = /^(\d{4})-(\d{2})$/.exec(iso);
  if (m2) return `${m2[2]}.${m2[1]}`;
  return iso;
}

/**
 * Plain React + CSS image viewer with mouse-wheel zoom, click-drag pan,
 * doubleclick-reset and +/-/↻ controls. No external dependency.
 */
function ZoomableImage({ src, alt }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragRef = useRef(null);

  const MIN = 1;
  const MAX = 6;
  const STEP = 0.25;

  const clamp = (v) => Math.max(MIN, Math.min(MAX, v));

  const reset = () => {
    setScale(1);
    setTx(0);
    setTy(0);
  };

  const zoomTo = (newScale, originX, originY) => {
    const wrap = wrapRef.current;
    if (!wrap) {
      setScale(clamp(newScale));
      return;
    }
    const rect = wrap.getBoundingClientRect();
    const cx = originX != null ? originX - rect.left - rect.width / 2 : 0;
    const cy = originY != null ? originY - rect.top - rect.height / 2 : 0;
    const next = clamp(newScale);
    // Adjust translate so the point under the cursor stays put.
    setTx((prev) => prev - cx * (next / scale - 1));
    setTy((prev) => prev - cy * (next / scale - 1));
    setScale(next);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? STEP : -STEP;
    zoomTo(scale + delta, e.clientX, e.clientY);
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTx: tx,
      startTy: ty,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setTx(dragRef.current.startTx + (e.clientX - dragRef.current.startX));
    setTy(dragRef.current.startTy + (e.clientY - dragRef.current.startY));
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full overflow-hidden bg-white select-none"
      onWheel={onWheel}
      onDoubleClick={reset}
      style={{ cursor: scale > 1 ? "grab" : "default", touchAction: "none" }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain pointer-events-auto"
        style={{
          transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: dragRef.current ? "none" : "transform 0.12s ease-out",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />

      {/* Zoom controls overlay (top-right) */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 bg-white/90 backdrop-blur-sm border border-ink-200 rounded-sm shadow-sm">
        <button
          type="button"
          onClick={() => zoomTo(scale + STEP)}
          aria-label="Zoom in"
          data-testid="compare-zoom-in"
          className="w-8 h-8 flex items-center justify-center text-ink-700 hover:bg-ink-50 transition-crisp border-b border-ink-200"
        >
          <Plus size={14} weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => zoomTo(scale - STEP)}
          aria-label="Zoom out"
          data-testid="compare-zoom-out"
          className="w-8 h-8 flex items-center justify-center text-ink-700 hover:bg-ink-50 transition-crisp border-b border-ink-200"
        >
          <Minus size={14} weight="bold" />
        </button>
        <button
          type="button"
          onClick={reset}
          aria-label="Zoom reset"
          data-testid="compare-zoom-reset"
          className="w-8 h-8 flex items-center justify-center text-ink-700 hover:bg-ink-50 transition-crisp"
          title="Reset (Doppelklick aufs Bild)"
        >
          <ArrowsClockwise size={13} weight="bold" />
        </button>
      </div>

      {/* Scale indicator (bottom-right) */}
      <div className="absolute bottom-2 right-2 z-10 bg-white/90 border border-ink-200 px-2 py-0.5 rounded-sm text-[10px] font-semibold tabular-nums text-ink-700">
        {Math.round(scale * 100)} %
      </div>

      {/* Help hint (bottom-left, only at scale 1) */}
      {scale <= 1 && (
        <div className="absolute bottom-2 left-2 z-10 text-[10px] uppercase tracking-wider font-semibold text-warm-600 bg-white/85 px-2 py-1 rounded-sm border border-ink-200">
          Mausrad zoomen · Doppelklick reset
        </div>
      )}
    </div>
  );
}

/**
 * Renders a .docx file as basic HTML via mammoth.js. Mammoth is loaded
 * lazily so the (~50 KB) library only ships when the user actually opens
 * a docx in the compare modal.
 */
function DocxPreview({ blob }) {
  const [html, setHtml] = useState("");
  const [err, setErr] = useState("");
  const [rotations, setRotations] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mammothMod = await import("mammoth/mammoth.browser");
        const arrayBuffer = await fetch(blob.url).then((r) => r.arrayBuffer());
        // Iter 19.68.13 — Rotations-Info aus DOCX-XML lesen, damit Bilder
        // (die Mammoth ohne Rotations-Attribute embeddet) mit CSS-Transform
        // korrekt rotiert werden. Reihenfolge im DOM matched die Reihenfolge
        // der <pic:pic>-Elemente in document.xml (Mammoth-Semantik).
        let rotDegrees = [];
        try {
          const JSZipMod = await import("jszip");
          const JSZip = JSZipMod.default || JSZipMod;
          const zip = await JSZip.loadAsync(arrayBuffer);
          const docXml = await zip.file("word/document.xml")?.async("string");
          if (docXml) {
            // Match <a:xfrm rot="..."> in document order (each <pic:pic>).
            const re = /<a:xfrm[^>]*\brot="(-?\d+)"/g;
            let m;
            while ((m = re.exec(docXml)) !== null) {
              const units = parseInt(m[1], 10) || 0;
              const deg = ((units / 60000) % 360 + 360) % 360;
              rotDegrees.push(deg);
            }
          }
        } catch (rotErr) {
          // Rotation-Parse ist Best-Effort — Mammoth-HTML wird trotzdem
          // gerendert, nur ohne Rotations-Korrektur.
          console.warn("[DocxPreview] rotation parse failed:", rotErr);
        }
        const result = await mammothMod.convertToHtml({ arrayBuffer });
        if (!cancelled) {
          setHtml(result.value || "<p><em>Leer.</em></p>");
          setRotations(rotDegrees);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Konvertierung fehlgeschlagen.");
      }
    })();
    return () => { cancelled = true; };
  }, [blob]);
  // Iter 19.68.13 — Nach dem HTML-Rendering CSS-Transform auf jedes <img>
  // anwenden, in der Reihenfolge der geparsten Rotationen. Ohne diesen
  // Post-Processing-Schritt zeigt Mammoth die Bild-Bytes im Original-
  // Querformat, obwohl Word das Bild um 90° gedreht als Hochformat zeigt.
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!wrapperRef.current || !rotations.length) return;
    const imgs = wrapperRef.current.querySelectorAll("img");
    imgs.forEach((img, i) => {
      const deg = rotations[i];
      if (deg && deg % 360 !== 0) {
        // rotate um deg Grad im Uhrzeigersinn (Word-Konvention).
        img.style.transform = `rotate(${deg}deg)`;
        // 90°/270° tauschen die Achsen — Bild wird sonst am Container
        // abgeschnitten. Wir setzen es in einen inline-block-Wrapper
        // und geben ihm Höhe/Breite proportional (nach Rotation).
        if (deg % 180 === 90 || deg % 180 === -90) {
          img.style.maxWidth = "none";
          img.style.transformOrigin = "center center";
          // Container-Padding gegen Cutoff:
          img.style.margin = "40px 0";
        }
      }
    });
  }, [html, rotations]);
  if (err) {
    return (
      <div className="p-6 text-sm text-warm-700 italic">
        Konnte die Word-Datei nicht in der Vorschau anzeigen ({err}).<br />
        Die Extraktion hat den Text aber trotzdem ausgewertet — Sie können
        die Datei lokal öffnen, um den Inhalt zu prüfen.
      </div>
    );
  }
  if (!html) {
    return <div className="p-6 text-sm text-warm-500 italic">Konvertiere Word-Dokument…</div>;
  }
  return (
    <div
      ref={wrapperRef}
      className="prose prose-sm max-w-none p-6 text-ink-900 leading-relaxed"
      // mammoth output is safe: it strips scripts, only structural HTML tags + inline styles.
      dangerouslySetInnerHTML={{ __html: html }}
      data-testid="compare-docx-preview"
    />
  );
}

/**
 * Full-screen modal (z-50) for side-by-side comparison: original source
 * document on the left, AI-extracted rows on the right. Files are read
 * from browser-RAM (blob: URLs) — nothing leaves the browser.
 */
export default function CompareModal({
  open,
  onClose,
  blobs,
  rows,
  stichtag,
  totalAnrechenbarDisplay,
  capMonths,
  capYears,
}) {
  const [activeIdx, setActiveIdx] = useState(0);
  if (!open) return null;
  const active = blobs[activeIdx] || blobs[0];
  const activeName = (active?.name || "").toLowerCase();
  const isPdf =
    (active?.type || "").toLowerCase().includes("pdf") ||
    activeName.endsWith(".pdf");
  const isDocx =
    (active?.type || "").toLowerCase().includes("wordprocessingml") ||
    activeName.endsWith(".docx");

  return (
    <div
      data-testid="tool-step3-compare-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-ink-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[1600px] h-[95vh] bg-white border border-ink-200 rounded-sm shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-ink-200">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
              Vergleichsansicht
            </div>
            <div className="flex items-baseline gap-3 mt-0.5 flex-wrap">
              <h3 className="font-cabinet text-xl text-ink-900 tracking-tight">
                Quelle vs. Extraktion
              </h3>
              {capMonths > 0 && (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-brand-300 bg-brand-100/40 rounded-sm text-[11px] font-semibold tabular-nums text-ink-900"
                  data-testid="compare-cap-pill"
                >
                  <span className="text-brand-600">{totalAnrechenbarDisplay || "0 T."}</span>
                  <span className="text-warm-500">von max. {capYears} J. Höchstgrenze</span>
                </span>
              )}
            </div>
            <p className="text-xs text-warm-700 mt-1 max-w-2xl">
              Prüfen Sie die Ergebnisse direkt gegen das hochgeladene Originaldokument.
              Die Datei wird nur im Arbeitsspeicher Ihres Browsers gehalten und nirgends gespeichert.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="tool-step3-compare-close"
            aria-label="Schließen"
            className="text-warm-600 hover:text-ink-900 transition-crisp shrink-0"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {blobs.length > 1 && (
          <div className="flex items-center gap-2 px-5 py-2 border-b border-ink-200 bg-ink-50 overflow-x-auto">
            {blobs.map((b, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIdx(i)}
                data-testid={`tool-step3-compare-tab-${i}`}
                className={`text-[12px] px-3 py-1.5 border transition-crisp whitespace-nowrap ${
                  i === activeIdx
                    ? "border-brand-500 bg-white text-ink-900 font-medium"
                    : "border-ink-200 bg-white text-warm-700 hover:border-ink-300"
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {/* Main 60/40 split. Left = source (more space for inspection), right = AI cards. */}
        <div className="grid grid-cols-1 lg:grid-cols-5 flex-1 min-h-0 overflow-hidden">
          <div className="lg:col-span-3 border-r border-ink-200 bg-ink-50 flex flex-col min-h-0">
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-semibold text-warm-500 shrink-0">
              Original — {active?.name}
            </div>
            <div className="flex-1 min-h-0 bg-white border border-ink-200 mx-2 mb-2 overflow-hidden">
              {active && isPdf ? (
                <iframe
                  src={active.url}
                  title={active.name}
                  className="w-full h-full"
                />
              ) : active && isDocx ? (
                <div className="w-full h-full overflow-auto bg-white">
                  <DocxPreview blob={active} />
                </div>
              ) : active ? (
                <ZoomableImage src={active.url} alt={active.name} />
              ) : (
                <div className="text-warm-600 text-sm p-6">Keine Datei verfügbar.</div>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 overflow-auto p-4 bg-white">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-2">
              Extraktion ({rows.length} Positionen)
            </div>
            <ol className="space-y-2">
              {rows.map((r, i) => {
                // Iter 19.63.36 — Authoritative Backend-Display-Strings.
                // Identisches Pattern wie in VordienstRow.jsx (Iter 19.63.27)
                // und BerechnungsdetailsModal.jsx — eliminiert FE-Math-Drift,
                // die hier bei Faktor < 1 die Roh-Range statt der hälftigen
                // Anrechnung zeigte (effective_period markiert nur die
                // Overlap-Resolution, NICHT den Faktor).
                const hasBackendDisplay = (
                  typeof r.display_raw === "string" ||
                  typeof r.display_anrechenbar === "string"
                );
                const hasBackendDays = (
                  typeof r.dauer_anrechenbar_jahre === "number" ||
                  typeof r.dauer_anrechenbar_monate === "number" ||
                  typeof r.dauer_anrechenbar_tage === "number"
                );

                // Fallback-Math nur für manuelle Frisch-Zeilen ohne
                // Backend-Roundtrip.
                // Iter 19.63.73 — Strikt 30/360-konforme Live-Preview,
                // matcht Backend bit-genau (kein Calendar↔30/360-Drift).
                const m = monthsBetween30360(r.datum_von, r.datum_bis, stichtag);
                const f = typeof r.anrechnungsfaktor === "number" ? r.anrechnungsfaktor : 1.0;
                const mE = typeof r.months_anrechenbar === "number"
                  ? r.months_anrechenbar
                  : (r.anrechenbar ? m * f : 0);

                let totalLabel, effLabel, isZero, isPartial;
                if (hasBackendDisplay) {
                  totalLabel = r.display_raw || "0 T.";
                  effLabel = r.display_anrechenbar || "0 T.";
                  if (!r.anrechenbar) {
                    isZero = true;
                  } else if (hasBackendDays) {
                    const backendDays =
                      (r.dauer_anrechenbar_jahre || 0) * 360 +
                      (r.dauer_anrechenbar_monate || 0) * 30 +
                      (r.dauer_anrechenbar_tage || 0);
                    isZero = backendDays < 0.001;
                  } else {
                    isZero = effLabel === "0 T." || effLabel === "0 T";
                  }
                  isPartial = !isZero && totalLabel !== effLabel;
                } else {
                  totalLabel = formatYMD(diffYMD(r.datum_von, r.datum_bis, stichtag));
                  isZero = !r.anrechenbar || mE <= 0.034;
                  isPartial = !isZero && mE < m - 0.034;
                  if (isZero) {
                    effLabel = "0 T.";
                  } else if (isPartial) {
                    const ep = r.effective_period;
                    if (ep && ep.von && ep.bis) {
                      effLabel = formatYMD(diffYMD(ep.von, ep.bis, stichtag));
                    } else {
                      effLabel = formatYMD(effectiveDurationYMD30360(mE));
                    }
                  } else {
                    effLabel = totalLabel;
                  }
                }

                const effClass = isZero
                  ? "text-warm-500 line-through"
                  : (isPartial ? "text-brand-600" : "text-ink-900");
                return (
                  <li
                    key={i}
                    data-testid={`tool-step3-compare-item-${i}`}
                    className={`border rounded-sm p-3 text-sm ${
                      !isZero ? "border-ink-200 bg-white" : "border-ink-200 bg-ink-50 opacity-80"
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="font-mono text-xs text-ink-700 tabular-nums">
                        {formatGermanDate(r.datum_von)} – {formatGermanDate(r.datum_bis)}
                      </span>
                      <span className={`text-sm font-semibold tabular-nums ${effClass}`}>
                        {effLabel}
                      </span>
                      {(isPartial || isZero) && (
                        <span className="text-[10px] text-warm-500 tabular-nums">
                          gesamt {totalLabel}
                        </span>
                      )}
                      {!r.anrechenbar && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-warm-600">
                          nicht anrechenbar
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-ink-900 font-medium">
                      {r.arbeitgeber || (
                        <span className="text-warm-500 italic">Kein Arbeitgeber</span>
                      )}
                    </div>
                    {r.taetigkeit && (
                      <div className="text-xs text-warm-700 mt-0.5">{r.taetigkeit}</div>
                    )}
                    {r.anrechenbarkeit_begruendung && (
                      <div className="text-[12px] text-warm-700 italic mt-2 leading-relaxed">
                        {r.anrechenbarkeit_begruendung}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
